/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react'
import { Flex, Select, SelectProps, Spin } from 'antd'
import { AntdFormItem, AntdFormItemProps } from '@/common/components/AntdForm/AntdFormItem.tsx'
import { EmployeeTooltip, EmployeeTooltipProps } from '@/common/components/EmployeeTooltip.tsx'
import { DeletableEmployeeCard } from '@/common/components/AntdForm/AntdFormEmployeeSelect.tsx'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { ClassValue } from 'clsx'
import { cn } from '@/utils/style.ts'
import { SearchOutlined } from '@ant-design/icons'
import { SelectOption } from '@/common/types/components.def.ts'
import { EmploymentStatus } from '@/common/types/employment.def.ts'

type EmployeeSearchProps = {
  value?: string | string[]
  onChange?: (args?: string | string[]) => void
  showDeleteCard?: boolean
  mode?: SelectProps['mode']
  disabled?: boolean
  disableLink?: boolean
  className?: ClassValue
  placeholder?: SelectProps['placeholder']
  extraOptions?: SelectOption[]
  labelFn?: EmployeeTooltipProps['labelFn']
  getUsername?: boolean
  allowedEntities?: string[]
  showActiveOnly?: boolean
}

type EmployeeOptionType = { key: string; label: React.ReactNode; value: string, searchKey: string }

export const EmployeeSearch: React.FC<EmployeeSearchProps> = ({
  value,
  onChange,
  disabled,
  mode,
  extraOptions,
  showDeleteCard,
  className,
  labelFn,
  getUsername,
  placeholder,
  allowedEntities,
  showActiveOnly,
}) => {
  const { data: publicInfo, isFetching } = useEmployeePublicInfo()
  const [search, setSearch] = useState<string>()

  const transformedExtraOptions = useMemo(() => {
    if (!extraOptions) return []
    return extraOptions.map(x => ({
      key: x.value,
      label: x.label,
      value: x.value,
      searchKey: (x.label as string).toLowerCase(),
    }))
  }, [extraOptions])

  const transformedOptions = useMemo<EmployeeOptionType[]>(() => {
    if (!publicInfo) return []

    const filteredPublicInfo = publicInfo
    .filter(x => !allowedEntities || allowedEntities.includes(x.employmentEntity || ''))
    .filter(x => !showActiveOnly || x.status === EmploymentStatus.Active)

    return filteredPublicInfo.map(x => {
      const searchText = `${x.employeeId}|${x.displayName}|${x.username || ''}|${x.title || ''}|${x.country || ''}`

      return {
        key: x.employeeId,
        label: <EmployeeTooltip
          employeeId={x.employeeId}
          className="block w-full"
          disableLink
          labelFn={labelFn
            ? labelFn
            : (x) => [`${x.displayName} (${x.employeeId})`, x.country || ''].filter(x => !!x).join(', ')}
        />,
        value: getUsername ? x.username : x.employeeId,
        searchKey: searchText.toLowerCase(),
      }
    })

  }, [labelFn, getUsername, publicInfo, allowedEntities])

  const filteredOptions = search
    ? [transformedExtraOptions, transformedOptions].flat().filter(x => x.searchKey.includes(search.toLowerCase()))
    : [transformedExtraOptions, transformedOptions].flat()

  return (
    <Flex vertical gap="small" className={cn(className)}>
      <Select
        className="w-full"
        suffixIcon={<SearchOutlined />}
        placeholder={placeholder || 'Search Employee by ID or Name'}
        filterOption={false}
        searchValue={search}
        onSearch={setSearch}
        notFoundContent={isFetching ? <Spin size="small" /> : null}
        mode={mode}
        allowClear
        showSearch
        value={value}
        onChange={onChange}
        disabled={disabled}
        onSelect={() => setSearch(undefined)}
        options={filteredOptions}
      />
      {!mode && showDeleteCard && value ?
        <DeletableEmployeeCard employeeId={value as string} onDelete={() => onChange?.(undefined)} /> : null}
    </Flex>
  )
}

type AntdFormEmployeeSearchProps = {
  showDeleteCard?: boolean
  mode?: SelectProps['mode']
  disabled?: boolean
  labelFn?: EmployeeSearchProps['labelFn']
  getUsername?: boolean
  extraOptions?: SelectOption[]
  allowedEntities?: string[]
  showActiveOnly?: boolean
} & AntdFormItemProps

export const AntdFormEmployeeSearch: React.FC<AntdFormEmployeeSearchProps> = ({
  fieldProps,
  // className,
  ...props
}) => {
  return (
    <AntdFormItem {...fieldProps}>
      <EmployeeSearch {...props} />
    </AntdFormItem>
  )
}